import { useState, useMemo } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import {
  avatarUrl,
  pickRandomGroup,
  tavernAvatarUrl,
  pickRandomTavernGroup,
  wupongAvatarUrl,
  pickRandomWupongGroup,
  WUPONG_AVATAR_POOL,
} from "@/lib/avatars";

/**
 * Two-step overlay: 1) pick one of N random avatar+name choices;
 * 2) confirm or rename. Calls onComplete({ seed, name }).
 */
export default function AvatarPicker({
  roleColor = "red",
  roleLabel = "Player",
  proceedLabel = "Proceed",
  pool = "default",
  theme = "medieval", // "medieval" | "wupong"
  onComplete,
  onCancel,
}) {
  const isWuPong = theme === "wupong";
  // Wu-Pong theme uses its own portrait pool; everything else uses tavern.
  const useTavern = pool === "tavern" && !isWuPong;
  const useWuPong = isWuPong;
  const [choices] = useState(() => {
    if (useWuPong) return pickRandomWupongGroup(WUPONG_AVATAR_POOL.length);
    if (useTavern) return pickRandomTavernGroup(15);
    return pickRandomGroup(15);
  });
  const resolveUrl = useWuPong ? wupongAvatarUrl : useTavern ? tavernAvatarUrl : avatarUrl;
  // Wu-Pong and tavern are both "portrait" (rectangular) styles; default is round.
  const isPortrait = useWuPong || useTavern;
  const [step, setStep] = useState("pick"); // 'pick' | 'confirm'
  const [picked, setPicked] = useState(null);
  const [name, setName] = useState("");

  const accent = useMemo(() => {
    if (isWuPong) {
      return {
        ring: "border-yellow-400",
        text: "text-yellow-200",
        glow: "shadow-[0_0_18px_rgba(250,204,21,0.35)]",
        chipBorder: "border-yellow-400/70",
      };
    }
    if (roleColor === "blue") {
      return {
        ring: "border-blue-500",
        text: "text-blue-200",
        glow: "shadow-[0_0_24px_rgba(67,102,184,0.5)]",
        chipBorder: "border-blue-400/60",
      };
    }
    return {
      ring: "border-red-500",
      text: "text-red-200",
      glow: "shadow-[0_0_24px_rgba(178,34,34,0.5)]",
      chipBorder: "border-red-400/60",
    };
  }, [roleColor, isWuPong]);

  const onPick = (entry) => {
    setPicked(entry);
    setName(entry.name);
    setStep("confirm");
  };

  const onProceed = () => {
    const trimmed = (name || picked.name).trim().slice(0, 20) || picked.name;
    onComplete({ seed: picked.seed, name: trimmed });
  };

  // Styling tokens per theme ---------------------------------------------------
  // Per user iter M642: picker pinned to top on small viewports so the
  // top selections aren't cut off when the panel is taller than the
  // phone screen.  Desktop preserves the original vertical centering.
  const overlayClass = isWuPong
    ? "fixed inset-0 z-50 flex items-start sm:items-center justify-center wupong-stage wupong-bg px-4 py-8 overflow-y-auto"
    : "fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/85 backdrop-blur-sm px-4 py-8 overflow-y-auto";
  const backBtnClass = isWuPong
    ? "wupong-ghost text-xs"
    : "arcade-btn arcade-btn-ghost !px-4 !py-2 text-xs";
  const panelClass = isWuPong
    ? `w-full ${step === "pick" ? "px-6 py-7" : "px-4 py-4"}`
    : `arcade-panel w-full ${step === "pick" ? "px-6 py-7" : "px-4 py-4"}`;
  const panelStyle = isWuPong
    ? {
        background: "rgba(26,24,20,0.85)",
        border: "2px solid #FACC15",
        boxShadow: "5px 5px 0 #DC2626",
      }
    : undefined;
  const cardClass = isWuPong
    ? `group flex flex-col items-center gap-1 px-1 pt-2 pb-1 transition-colors`
    : `group flex flex-col items-center gap-1 px-1 pt-2 pb-1 rounded-md border-2 ${accent.chipBorder} bg-amber-50/40 hover:bg-amber-100/70 transition-colors`;
  const cardStyle = isWuPong
    ? {
        border: "2px solid rgba(250,204,21,0.7)",
        background: "rgba(10,9,7,0.65)",
        boxShadow: "3px 3px 0 #DC2626",
      }
    : undefined;
  const nameClass = isWuPong
    ? "wupong-stencil tracking-wide uppercase text-[10px] text-center leading-tight px-0.5"
    : "font-display tracking-wide uppercase text-xs text-stone-900 text-center leading-tight px-0.5";
  const nameStyle = isWuPong ? { color: "#FACC15" } : undefined;

  return (
    <div
      className={isWuPong ? "wupong-stage " + overlayClass : overlayClass}
      data-testid="avatar-picker-overlay"
    >
      {isWuPong && <div className="wupong-grain pointer-events-none absolute inset-0" />}
      <button
        onClick={onCancel}
        className={backBtnClass}
        style={{ position: "fixed", top: "1rem", left: "1rem", zIndex: 60 }}
        data-testid="avatar-cancel-button"
      >
        <ArrowLeft size={14} className="inline -mt-0.5 mr-1" /> {isWuPong ? "BACK" : "Back"}
      </button>
      <div className={`relative w-full ${step === "pick" ? "max-w-4xl" : "max-w-md"} flex flex-col items-center`}>
        {step === "pick" && (
          isWuPong ? (
            <h1
              className="wupong-headline text-5xl md:text-6xl text-center mb-6"
              style={{
                color: "#FACC15",
                textShadow: "3px 3px 0 #DC2626",
              }}
              data-testid="avatar-picker-title"
            >
              CLAIM YA VISAGE
            </h1>
          ) : (
            <>
              <h1
                className="font-display text-3xl md:text-4xl font-bold tracking-tight text-center mb-2"
                style={{
                  color: "#E5C870",
                  textShadow: "0 1px 0 rgba(0,0,0,0.7), 0 0 14px rgba(201,162,74,0.5)",
                }}
                data-testid="avatar-picker-title"
              >
                Choose Thy Visage
              </h1>
              <p
                className="text-center mb-6 italic"
                style={{
                  fontFamily: "'IM Fell English','EB Garamond','Garamond','Georgia',serif",
                  color: "#B49770",
                  fontSize: "0.95rem",
                  letterSpacing: "0.01em",
                }}
                data-testid="avatar-picker-subtitle"
              >
                These visages were imagined and rendered by Emergent AI.
              </p>
            </>
          )
        )}
        <div className={panelClass} style={panelStyle}>
          {step === "pick" ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {choices.map((p) => (
                  <button
                    key={p.seed}
                    onClick={() => onPick(p)}
                    className={cardClass}
                    style={cardStyle}
                    data-testid={`avatar-card-${p.seed}`}
                  >
                    <img
                      src={resolveUrl(p.seed)}
                      alt={p.name}
                      className={
                        isPortrait
                          ? `w-24 h-28 object-cover rounded-sm border-2 ${accent.ring} bg-stone-800 ${accent.glow}`
                          : `w-24 h-24 rounded-full border-2 ${accent.ring} bg-amber-200/40 ${accent.glow}`
                      }
                      style={
                        isPortrait
                          ? { filter: isWuPong ? "contrast(1.1) saturate(0.85) brightness(0.97)" : "contrast(1.25) saturate(0.7) sepia(0.15) brightness(0.95)" }
                          : undefined
                      }
                      draggable={false}
                    />
                    <div className={nameClass} style={nameStyle}>
                      {p.name}
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              {isWuPong ? (
                <h3
                  className="wupong-headline text-3xl md:text-4xl text-center mb-1"
                  style={{ color: "#FACC15", textShadow: "2px 2px 0 #DC2626" }}
                >
                  YA NAME?
                </h3>
              ) : (
                <h3
                  className="font-display text-2xl md:text-3xl uppercase text-center mb-1"
                  style={{ color: "#7A1F1F" }}
                >
                  Thy Name?
                </h3>
              )}
              <p
                className={isWuPong ? "text-center wupong-typewriter text-xs uppercase tracking-widest mb-3" : "text-center text-stone-700 italic font-body text-sm mb-3"}
                style={isWuPong ? { color: "#E5C870", opacity: 0.85 } : undefined}
              >
                {isWuPong ? "Visage locked in — name ya self, or roll with what's given." : "The likeness is set — name thyself or take the one already given."}
              </p>
              <div className="flex flex-col items-center gap-2">
                <img
                  src={resolveUrl(picked.seed)}
                  alt={picked.name}
                  className={
                    isPortrait
                      ? `w-32 h-40 object-cover rounded-sm border-4 ${accent.ring} bg-stone-800 ${accent.glow}`
                      : `w-32 h-32 rounded-full border-4 ${accent.ring} bg-amber-200/40 ${accent.glow}`
                  }
                  style={
                    isPortrait
                      ? { filter: isWuPong ? "contrast(1.1) saturate(0.85) brightness(0.97)" : "contrast(1.25) saturate(0.7) sepia(0.15) brightness(0.95)" }
                      : undefined
                  }
                  draggable={false}
                  data-testid="avatar-confirm-image"
                />
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value.slice(0, 20))}
                  maxLength={20}
                  className={isWuPong ? "w-full text-center text-lg" : "arcade-input w-full text-center text-lg"}
                  style={
                    isWuPong
                      ? {
                          background: "transparent",
                          border: "none",
                          borderBottom: "2px solid #FACC15",
                          color: "#FACC15",
                          outline: "none",
                          padding: "0.5rem 0",
                          fontFamily: "'Special Elite', 'Courier New', monospace",
                          letterSpacing: "0.1em",
                        }
                      : undefined
                  }
                  placeholder={isWuPong ? "Ya name…" : "Thy name…"}
                  data-testid="avatar-name-input"
                  autoFocus
                />
                <div className="flex gap-3 mt-1">
                  <button
                    onClick={() => setStep("pick")}
                    className={isWuPong ? "wupong-ghost text-xs" : "arcade-btn arcade-btn-ghost !px-5 !py-2 text-xs"}
                    data-testid="avatar-back-button"
                  >
                    <ArrowLeft size={14} className="inline -mt-0.5 mr-1" /> {isWuPong ? "RE-PICK" : "Re-pick"}
                  </button>
                  <button
                    onClick={onProceed}
                    disabled={!name.trim()}
                    className={isWuPong ? "wupong-cta !text-base !px-5 !py-2 disabled:opacity-50" : "arcade-btn arcade-btn-magenta !px-6 !py-2 text-xs disabled:opacity-50"}
                    data-testid="avatar-proceed-button"
                  >
                    {proceedLabel} <ArrowRight size={14} className="inline -mt-0.5 ml-1" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
